import React, { useEffect, useState } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import MobileBottomNav from './components/MobileBottomNav';
import MobileSearchBar from './components/MobileSearchBar';
import SkipToContent from './components/SkipToContent';
import ComingSoon from './components/ComingSoon';
import ChatPopup from './components/ChatPopup';
import Home from './pages/Home';
import About from './pages/About';
import LocationCoffsHarbour from './pages/LocationCoffsHarbour';
import LocationTelehealth from './pages/LocationTelehealth';
import Clinics from './pages/Clinics';
import Services from './pages/Services';
import FAQs from './pages/FAQs';
import Bookings from './pages/Bookings';
import Contact from './pages/Contact';
import Refer from './pages/Refer';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import Summary from './pages/Summary';
import FormReferral from './pages/FormReferral';
import FormClientUpload from './pages/FormClientUpload';
import FormContact from './pages/FormContact';
import FormMonivaeBooking from './pages/FormMonivaeBooking';
import FormMonivaeReferral from './pages/FormMonivaeReferral';
import FormMonivaeContact from './pages/FormMonivaeContact';
import FormNDIS from './pages/FormNDIS';
import LGBTIQ from './pages/LGBTIQ';
import AutismAssessment from './pages/AutismAssessment';
import AdhdAssessment from './pages/AdhdAssessment';
import FasdAssessment from './pages/FasdAssessment';
import LearningAssessment from './pages/LearningAssessment';
import WhatToExpect from './pages/WhatToExpect';
import Therapy from './pages/Therapy';
import Schools from './pages/Schools';
import Referrers from './pages/Referrers';
import Fees from './pages/Fees';
import VisitingClinicHamilton from './pages/VisitingClinicHamilton';
import VisitingClinicPortMacquarie from './pages/VisitingClinicPortMacquarie';
import VisitingClinicSydney from './pages/VisitingClinicSydney';
import VisitingClinicMelbourne from './pages/VisitingClinicMelbourne';
import VisitingClinicGoldCoast from './pages/VisitingClinicGoldCoast';
import NotFound from './pages/NotFound';
import FormPage from './components/FormPage';

// Check if maintenance mode is enabled
const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true';

// Component to scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Scroll to top instantly when route changes
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);

  return null;
}

// Layout wrapper component to conditionally show header/footer
function Layout({ children }) {
  const location = useLocation();
  // Treat "/summary/" exactly like "/summary" (Azure serves both), so a
  // trailing slash never adds the main header and footer on top of a page's own.
  const path = location.pathname.replace(/\/+$/, '') || '/';
  const isStandaloneForm = path.startsWith('/form/');
  const isSummaryPage = path === '/summary';
  const isLGBTIQPage = path === '/lgbtqia';
  const isMonivaeRoot = window.location.hostname === 'monivae.clinicalminds.net' && path === '/';
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const handleMenuClick = () => {
    // Trigger the mobile menu toggle in Header
    const menuButton = document.querySelector('.mobile-menu-toggle');
    if (menuButton) {
      menuButton.click();
    }
  };

  const handleSearchClick = () => {
    setIsSearchOpen(true);
  };

  const handleSearchClose = () => {
    setIsSearchOpen(false);
  };

  // /lgbtqia keeps its own simple header but uses the main site footer (round 3e)
  if (isLGBTIQPage) {
    return (
      <>
        {children}
        <Footer />
      </>
    );
  }

  // For standalone forms, the summary page and the Monivae root, render only the children without header/footer
  if (isStandaloneForm || isSummaryPage || isMonivaeRoot) {
    return children;
  }

  // For regular pages, render with header/footer
  return (
    <>
      <SkipToContent />
      <Header />
      {children}
      <Footer />
      <MobileBottomNav onMenuClick={handleMenuClick} onSearchClick={handleSearchClick} />
      <MobileSearchBar isOpen={isSearchOpen} onClose={handleSearchClose} />
      <ChatPopup />
      <ScrollNavigation />
    </>
  );
}

// Scroll Navigation Component
function ScrollNavigation() {
  const [showNav, setShowNav] = useState(false);
  const [showScrollUp, setShowScrollUp] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.pageYOffset;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // Show navigation after scrolling down a bit
      setShowNav(scrollPosition > 300);

      // Show scroll up button when not at top
      setShowScrollUp(scrollPosition > 300);

      // Show scroll down button when not at bottom
      setShowScrollDown(scrollPosition + windowHeight < documentHeight - 100);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollDown = () => {
    const currentPosition = window.pageYOffset;
    const windowHeight = window.innerHeight;
    window.scrollTo({ top: currentPosition + windowHeight * 0.8, behavior: 'smooth' });
  };

  if (!showNav) return null;

  return (
    <div className="scroll-nav-container">
      {showScrollUp && (
        <button 
          className="scroll-nav-button" 
          onClick={scrollToTop}
          aria-label="Scroll to top"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 15l-6-6-6 6"/>
          </svg>
        </button>
      )}
      {showScrollDown && (
        <button 
          className="scroll-nav-button" 
          onClick={scrollDown}
          aria-label="Scroll down"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </button>
      )}
    </div>
  );
}

// Component to check subdomain and render appropriate home page
function HomeOrSummary() {
  const hostname = window.location.hostname;
  
  // If accessing via Monivae subdomain, show Summary page at root
  if (hostname === 'monivae.clinicalminds.net') {
    return <Summary />;
  }
  
  // Otherwise show regular Home page
  return <Home />;
}

function App() {
  // If maintenance mode is enabled, show only the Coming Soon page
  if (isMaintenanceMode) {
    return (
      <div className="App">
        <ComingSoon />
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Layout>
          <Routes>
            <Route path="/" element={<HomeOrSummary />} />
            <Route path="/about" element={<About />} />
            <Route path="/locations/coffs-harbour" element={<LocationCoffsHarbour />} />
            <Route path="/locations/telehealth" element={<LocationTelehealth />} />
            <Route path="/clinics" element={<Clinics />} />
            <Route path="/visiting-clinics/hamilton" element={<VisitingClinicHamilton />} />
            <Route path="/visiting-clinics/port-macquarie" element={<VisitingClinicPortMacquarie />} />
            <Route path="/visiting-clinics/sydney" element={<VisitingClinicSydney />} />
            <Route path="/visiting-clinics/melbourne" element={<VisitingClinicMelbourne />} />
            <Route path="/visiting-clinics/gold-coast" element={<VisitingClinicGoldCoast />} />
            {/* Redirects for old URLs */}
            <Route path="/locations/sydney" element={<Navigate to="/clinics" replace />} />
            <Route path="/locations/corporate" element={<Navigate to="/" replace />} />
            <Route path="/locations/branch-1" element={<Navigate to="/locations/coffs-harbour" replace />} />
            <Route path="/locations/branch-2" element={<Navigate to="/locations/telehealth" replace />} />
            <Route path="/locations/branch-3" element={<Navigate to="/clinics" replace />} />
            <Route path="/locations/branch-4" element={<Navigate to="/" replace />} />
            <Route path="/corporate" element={<Navigate to="/" replace />} />
            <Route path="/services" element={<Services />} />
            <Route path="/autism-assessment" element={<AutismAssessment />} />
            <Route path="/adhd-assessment" element={<AdhdAssessment />} />
            <Route path="/fasd-assessment" element={<FasdAssessment />} />
            <Route path="/learning-assessment" element={<LearningAssessment />} />
            <Route path="/what-to-expect" element={<WhatToExpect />} />
            <Route path="/therapy" element={<Therapy />} />
            <Route path="/schools" element={<Schools />} />
            <Route path="/referrers" element={<Referrers />} />
            <Route path="/fees" element={<Fees />} />
            <Route path="/faqs" element={<FAQs />} />
            <Route path="/bookings" element={<Bookings />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/refer" element={<Refer />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            {/* /links is retired: send anyone with an old link to the homepage */}
            <Route path="/links" element={<Navigate to="/" replace />} />
            {/* Hidden routes - not in navigation but accessible via direct URL */}
            <Route path="/summary" element={<Summary />} />
            {/* /school-flyer is retired: send anyone with an old link to the homepage */}
            <Route path="/school-flyer" element={<Navigate to="/" replace />} />
            <Route path="/lgbtqia" element={<LGBTIQ />} />
            {/* Standalone form-only pages (no header/footer for embedding) */}
            <Route path="/form/referral" element={<FormPage title="Referral form" path="referral"><FormReferral /></FormPage>} />
            <Route path="/form/client-upload" element={<FormPage title="Family document upload" path="client-upload"><FormClientUpload /></FormPage>} />
            <Route path="/form/contact" element={<FormPage title="Contact form" path="contact"><FormContact /></FormPage>} />
            {/* Monivae standalone forms */}
            <Route path="/form/monivae-booking" element={<FormPage title="Monivae booking form" path="monivae-booking"><FormMonivaeBooking /></FormPage>} />
            <Route path="/form/monivae-referral" element={<FormPage title="Monivae referral form" path="monivae-referral"><FormMonivaeReferral /></FormPage>} />
            <Route path="/form/monivae-contact" element={<FormPage title="Monivae contact form" path="monivae-contact"><FormMonivaeContact /></FormPage>} />
            {/* NDIS standalone form */}
            <Route path="/form/ndis" element={<FormPage title="NDIS referral form" path="ndis"><FormNDIS /></FormPage>} />
            {/* Page not found: /404 is pre-rendered and served by Azure for any unknown address */}
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Layout>
      </BrowserRouter>
    </div>
  );
}

export default App;
